import { useMemo } from "react";
import { Dayjs } from "dayjs";
import { getReadyDateDetails } from "../configs/checkoutDateRules";
import { useAppSelector } from "./reduxHooks";
import { IStoreClosure } from "../util/store-closures";

const defaultWorkingDays = [
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
];

const parseTime = (time: string) => {
  const [hours, minutes, seconds] = time.split(":").map(Number);
  const date = new Date();
  date.setHours(hours, minutes, seconds, 0);
  return date;
};

export interface IDateHookResult {
  /**
   * Best-known date. While store config hasn't loaded yet this is derived
   * from hardcoded defaults (a guess) - check `isReady` before treating it
   * as authoritative.
   */
  date: Dayjs;
  /**
   * True once the store config request has settled (succeeded or failed).
   * Only then is `date` derived from real config (or, if the request
   * failed, from the hardcoded defaults used as a genuine last resort).
   * False while the config is still in flight - callers must not treat
   * `date` as final in that state.
   */
  isReady: boolean;
  /** Holidays (store closure dates) the date had to skip */
  holidays: IStoreClosure[];
  /** True when the date is the one the admin set on a skipped holiday */
  isAdminDate: boolean;
}

export const useSpecialDate = (): IDateHookResult => {
  const storeConfig = useAppSelector(
    (state) => state.store.getStoreConfigResponse
  );
  const storeConfigError = useAppSelector(
    (state) => state.store.getStoreConfigError
  );

  const isReady = !!storeConfig || !!storeConfigError;

  // Worked out on every render (cheap) so it follows the clock - a POS left
  // open past the cut-off or overnight kept showing the old dates. Memoised on
  // the value so its identity only changes when the date does.
  const body = storeConfig?.body;
  const details = getReadyDateDetails({
    isSpecialDay: true,
    checkInCutOffTime: parseTime(body?.cutOffTime || "10:00:00"),
    pickupTime: parseTime(body?.pickupTime || "15:00:00"),
    workingDays: body?.workingDays || defaultWorkingDays,
    generalServiceDays: body?.daysToDeliver ?? 2,
    specialServiceDays: body?.specialDaysToDeliver ?? 0,
  });
  const fresh = details.date;
  const freshKey = fresh.format("YYYY-MM-DD HH:mm");
  // Holidays it skipped, for the pin tag modal's explanation (3.f)
  const holidaysKey = details.holidays.map((h) => h.date).join(",");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const holidays = useMemo(() => details.holidays, [holidaysKey]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const specialDate = useMemo(() => fresh, [freshKey]);

  return {
    date: specialDate,
    isReady,
    holidays,
    isAdminDate: details.isAdminDate,
  };
};
